import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Settings, Link2, ExternalLink, FileDown, Check, X } from 'lucide-react';

const SectionTools = ({ sectionId, sectionTitle }) => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const menuRef = useRef(null);

  const labels = {
    en: {
      copyLink: 'Copy Link to Section',
      openStandalone: 'Open as Standalone',
      exportPdf: 'Export to PDF',
      copied: 'Link Copied!'
    },
    fr: {
      copyLink: 'Copier le Lien',
      openStandalone: 'Ouvrir Séparément',
      exportPdf: 'Exporter en PDF',
      copied: 'Lien Copié!'
    }
  };

  const t = labels[language];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${sectionId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setIsOpen(false);
      }, 1500);
    });
  };

  const handleOpenStandalone = () => {
    const url = `${window.location.origin}${window.location.pathname}#${sectionId}`;
    window.open(url, '_blank');
    setIsOpen(false);
  };

  const handleExportPdf = () => {
    // Print current section
    const section = document.getElementById(sectionId);
    if (section) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${sectionTitle} - YUL On Tap</title>
            <style>
              body { font-family: 'Manrope', sans-serif; padding: 40px; background: #fff; color: #1a1a1a; }
              h1, h2, h3 { font-family: 'Playfair Display', serif; }
              img { max-width: 100%; }
            </style>
          </head>
          <body>
            <h1>YUL On Tap</h1>
            <h2>${sectionTitle}</h2>
            <hr/>
            <p>Exported from: ${window.location.href}</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-200"
        aria-label="Section tools"
        data-testid={`section-tools-${sectionId}`}
      >
        <Settings className="w-4 h-4 text-white/60 hover:text-white/80" strokeWidth={1.5} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
          {copySuccess ? (
            <div className="flex items-center gap-2 px-4 py-3 text-green-400">
              <Check className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm">{t.copied}</span>
            </div>
          ) : (
            <>
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors text-left"
              >
                <Link2 className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">{t.copyLink}</span>
              </button>
              <button
                onClick={handleOpenStandalone}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors text-left border-t border-white/5"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">{t.openStandalone}</span>
              </button>
              <button
                onClick={handleExportPdf}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors text-left border-t border-white/5"
              >
                <FileDown className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">{t.exportPdf}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionTools;
